import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDrawer } from "@/context/DrawerContext";
import { TierBadge } from "@/components/TierBadge";
import { Sun, AlertCircle, Zap, ChevronRight, Clock, CheckCircle2 } from "lucide-react";

function TaskRow({ t, onOpen }) {
  const overdue = t.status === "overdue";
  return (
    <button onClick={onOpen} data-testid={`myday-task-${t.id}`}
      className="w-full text-left bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-4 hover:-translate-y-0.5 hover:shadow-md transition-all duration-150 group">
      <div className="w-11 text-center shrink-0">
        <div className="font-mono text-lg font-bold tabular-nums leading-none">{t.fit_score}</div>
        <div className="text-[9px] text-zinc-400 mt-0.5">SCORE</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm truncate">{t.name}</span>
          <TierBadge tier={t.tier} />
        </div>
        <div className="text-xs text-zinc-500 truncate">{t.company} · {t.stage}</div>
        {t.next_action && (
          <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-[#B23600]">
            <ChevronRight className="h-3.5 w-3.5" /> {t.next_action}
          </div>
        )}
      </div>
      {t.follow_up_date && (
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-1 rounded-md border ${overdue ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
          <Clock className="h-3 w-3" /> {overdue ? "overdue" : "today"}{t.follow_up_time ? ` · ${t.follow_up_time}` : ""}
        </span>
      )}
      <ChevronRight className="h-4 w-4 text-zinc-300 group-hover:text-klein transition-colors shrink-0" />
    </button>
  );
}

export default function MyDay() {
  const { user } = useAuth();
  const { openLead, refreshKey } = useDrawer();
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/my-day").then((r) => setD(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  if (!d) return <div className="p-8 font-mono text-sm text-zinc-500">Loading your day…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-9 w-9 rounded-xl bg-klein text-white grid place-items-center"><Sun className="h-5 w-5" /></div>
        <h1 className="font-head text-3xl font-black tracking-tight">{greeting}, {user?.name?.split(" ")[0]}</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        {d.counts.due} follow-up{d.counts.due !== 1 ? "s" : ""} due · {d.counts.priority} high-priority lead{d.counts.priority !== 1 ? "s" : ""} to action today.
      </p>

      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <h2 className="font-head font-bold text-sm uppercase tracking-wide">Due today &amp; overdue</h2>
          <span className="font-mono text-xs bg-red-600 text-white rounded-md px-1.5">{d.due.length}</span>
        </div>
        <div className="space-y-2" data-testid="myday-due">
          {d.due.map((t) => <TaskRow key={t.id} t={t} onOpen={() => openLead(t.id)} />)}
          {d.due.length === 0 && (
            <div className="bg-white border border-dashed border-zinc-200 rounded-xl p-8 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">Nothing due. You're all caught up.</p>
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-4 w-4 text-[#FF4F00]" />
          <h2 className="font-head font-bold text-sm uppercase tracking-wide">High-priority next actions</h2>
          <span className="font-mono text-xs bg-zinc-900 text-white rounded-md px-1.5">{d.priority.length}</span>
        </div>
        <div className="space-y-2" data-testid="myday-priority">
          {d.priority.map((t) => <TaskRow key={t.id} t={t} onOpen={() => openLead(t.id)} />)}
          {d.priority.length === 0 && (
            <div className="bg-white border border-dashed border-zinc-200 rounded-xl p-8 text-center text-sm text-zinc-500">No high-priority leads waiting.</div>
          )}
        </div>
      </section>
    </div>
  );
}
